import { useEffect, useMemo, useRef, useState } from "react";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";
import { firebaseApp } from "../firebase/firebaseApp";
import { classifyKindFromVariant } from "../utils/storagePaths";

// Utility: convert object-of-arrays => nested arrays, sorted by numeric keys
function objectOfArraysToNestedArrays(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
  const keys = Object.keys(obj).sort((a, b) => Number(a) - Number(b));
  return keys.map((k) => (Array.isArray(obj[k]) ? obj[k] : []));
}

// Utility: hydrate Firestore payload back to the original JSON shape
function hydrateFromFirestoreDoc(d) {
  if (!d || typeof d !== "object") return d;

  const hydrated = { ...d };

  // Rebuild best_traces if it was stored as object-of-arrays
  if (
    d.best_traces &&
    !Array.isArray(d.best_traces) &&
    typeof d.best_traces === "object"
  ) {
    const nested = objectOfArraysToNestedArrays(d.best_traces);
    if (nested) hydrated.best_traces = nested;
  }

  return hydrated;
}

/**
 * useActivityData
 *
 * @param {object} params
 * @param {string} params.uid
 * @param {string} params.baseName
 * @param {string} [params.tag="breatheultra"]
 * @param {boolean} [params.fetchBlob=true]
 */
export function useActivityData({
  uid,
  baseName,
  tag = "breatheultra",
  fetchBlob = true,
}) {
  const [state, setState] = useState({
    loadingIndex: true,
    loadingBlob: false,
    loading: true,
    error: null,
    notFound: false,
    activity: null,
    data: null,
    downloadURL: null,
  });

  const db = useMemo(() => getFirestore(firebaseApp), []);
  const storage = useMemo(() => getStorage(firebaseApp), []);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // Function to update the Firestore document with new data
  const updateActivityData = useMemo(() => {
    return async (newData) => {
      if (!uid || !baseName) {
        throw new Error("Cannot update data: missing UID or baseName.");
      }

      const idxRef = doc(db, "measurements", uid, tag, baseName);
      try {
        await updateDoc(idxRef, newData);
        // Optimistically update the local state with the new data
        setState((s) => ({
          ...s,
          data: {
            ...s.data,
            ...newData,
          },
        }));
      } catch (e) {
        console.error("Error updating activity data:", e);
        throw new Error("Failed to save activity details.");
      }
    };
  }, [db, uid, baseName, tag]);

  useEffect(() => {
    async function run() {
      if (!uid || !baseName) {
        setState((s) => ({
          ...s,
          loadingIndex: false,
          loading: false,
          notFound: true,
          error: null,
          activity: null,
        }));
        return;
      }

      try {
        // 1) Read Firestore index/payload
        const idxRef = doc(db, "measurements", uid, tag, baseName);
        const snap = await getDoc(idxRef);

        if (!snap.exists()) {
          if (!cancelledRef.current) {
            setState({
              loadingIndex: false,
              loadingBlob: false,
              loading: false,
              error: null,
              notFound: true,
              activity: null,
              data: null,
              downloadURL: null,
            });
          }
          return;
        }

        const d = snap.data() || {};
        const tsMs = Number.isFinite(d.ts) ? d.ts : 0;
        const date = tsMs ? new Date(tsMs) : undefined;
        const variant = d.variant || "";
        const kind = classifyKindFromVariant(variant);

        const activity = {
          uid,
          baseName,
          timestamp: tsMs,
          date,
          variant,
          kind,
          status: "indexed",
        };

        if (cancelledRef.current) return;

        const hydrated = hydrateFromFirestoreDoc(d);
        const hasEmbeddedSpiro =
          Array.isArray(d.trace) ||
          Array.isArray(d.times) ||
          Array.isArray(d?.best_traces?.[0]) ||
          (d.best_traces && typeof d.best_traces === "object");

        const hasEmbeddedExertion =
          Array.isArray(d?.peak_times_s) ||
          Array.isArray(d?.decay_fit?.exp?.yhat) ||
          Number.isFinite(d?.estimated_rr_bpm) ||
          Number.isFinite(d?.duration_s_full);

        const hasEmbedded =
          !d.heavy_fields_omitted && (hasEmbeddedSpiro || hasEmbeddedExertion);

        // If we are not allowed to hit Storage, still return whatever Firestore gave us.
        if (!fetchBlob) {
          if (!cancelledRef.current) {
            setState((s) => ({
              ...s,
              loadingIndex: false,
              loadingBlob: false,
              loading: false,
              error: null,
              notFound: false,
              activity,
              // Return hydrated Firestore doc even if "embedded" check is false.
              data: hydrated ?? null,
              downloadURL: null,
            }));
          }
          return;
        }

        // We will fetch from Storage; set intermediate state first.
        setState((s) => ({
          ...s,
          loadingIndex: false,
          loadingBlob: true,
          loading: true,
          error: null,
          notFound: false,
          activity,
        }));

        // If Firestore already contains embedded data, use it and skip Storage.
        if (hasEmbedded) {
          if (!cancelledRef.current) {
            setState((s) => ({
              ...s,
              loadingBlob: false,
              loading: false,
              data: hydrated,
              downloadURL: null,
            }));
          }
          return;
        }
      } catch (e) {
        if (!cancelledRef.current) {
          setState({
            loadingIndex: false,
            loadingBlob: false,
            loading: false,
            error: e,
            notFound: true,
            activity: null,
            data: null,
            downloadURL: null,
          });
        }
      }
    }

    run();
  }, [db, storage, uid, baseName, tag, fetchBlob]);

  return { ...state, updateActivityData };
}
